// GLOBAL SPRITE CACHE FOR PSY BLOCK EFFECT
const psyBlockSpriteCache = new Map<string, HTMLCanvasElement>();

function getPsyPulseSprite(): HTMLCanvasElement {
    const key = `pulse`;
    let sprite = psyBlockSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = 30;
        sprite.height = 30;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = 15;
            const centerY = 15;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, 5, 0, Math.PI * 2);
            sCtx.fillStyle = 'rgba(56, 189, 248, 1)';
            sCtx.shadowBlur = 10;
            sCtx.shadowColor = 'rgba(56, 189, 248, 1)';
            sCtx.fill();
        }
        psyBlockSpriteCache.set(key, sprite);
    }
    return sprite;
}

export type GooNode = {
    x: number
    y: number
    vx: number
    vy: number
    size: number
    baseSize: number
    phase: number
}

export type GooConnection = {
    from: number
    to: number
    length: number
    pulses: { t: number; speed: number }[]
}

export type EdgeConnection = {
    nodeIndex: number
    x: number
    y: number
    pulses: { t: number; speed: number }[]
}

export type PsyBlockData = {
    nodes: GooNode[]
    connections: GooConnection[]
    edgeConnections: EdgeConnection[]
    targetX?: number
    targetY?: number
}

export type PsyBlockEffect = {
    kind: 'psyBlock'
    t: number
    x: number
    y: number
    data: PsyBlockData
}

export function createPsyBlockEffect(x: number, y: number): PsyBlockEffect {
    const cx = window.innerWidth / 2
    const cy = window.innerHeight / 2
    const nodes: GooNode[] = []
    const nodeCount = 15

    // Nodes start scattered but generally central
    for (let i = 0; i < nodeCount; i++) {
        nodes.push({
            x: cx + (Math.random() - 0.5) * 300,
            y: cy + (Math.random() - 0.5) * 300,
            vx: (Math.random() - 0.5) * 1,
            vy: (Math.random() - 0.5) * 1,
            size: 6 + Math.random() * 8,
            baseSize: 6 + Math.random() * 8,
            phase: Math.random() * Math.PI * 2
        })
    }

    const connections: GooConnection[] = []
    // Intertwined central network
    for (let i = 0; i < nodeCount; i++) {
        const neighbors = Math.floor(2 + Math.random() * 2)
        for (let j = 0; j < neighbors; j++) {
            const target = (i + 1 + Math.floor(Math.random() * (nodeCount - 2))) % nodeCount
            if (!connections.some(c => (c.from === i && c.to === target) || (c.from === target && c.to === i))) {
                connections.push({ from: i, to: target, length: 50 + Math.random() * 80, pulses: [] })
            }
        }
    }

    const edgeConnections: EdgeConnection[] = []
    // Many lines from off-screen
    for (let i = 0; i < 20; i++) {
        const nodeIndex = Math.floor(Math.random() * nodeCount)
        const side = Math.floor(Math.random() * 4)
        let ex = 0, ey = 0
        // Spawn further off-screen for dramatic entry
        const offset = 100
        if (side === 0) { ex = Math.random() * window.innerWidth; ey = -offset }
        else if (side === 1) { ex = window.innerWidth + offset; ey = Math.random() * window.innerHeight }
        else if (side === 2) { ex = Math.random() * window.innerWidth; ey = window.innerHeight + offset }
        else { ex = -offset; ey = Math.random() * window.innerHeight }

        edgeConnections.push({ nodeIndex, x: ex, y: ey, pulses: [] })
    }

    // Initial pulses waiting at the EDGES (start of lines)
    edgeConnections.forEach(e => {
        if (Math.random() < 0.8) {
            // t=0 is at the edge. Speed 0 means they wait.
            e.pulses.push({ t: 0, speed: 0 })
        }
    })

    return {
        kind: 'psyBlock',
        t: 0,
        x, y,
        data: { nodes, connections, edgeConnections, targetX: x, targetY: y }
    }
}

export function renderPsyBlockEffect(
    effect: PsyBlockEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: any) => void
): boolean {
    const maxTime = 2500
    const tt = effect.t / maxTime
    if (tt > 1.2) return false
    const data = effect.data
    const targetX = data.targetX!
    const targetY = data.targetY!

    // Physics Update
    data.nodes.forEach((node, idx) => {
        node.size = node.baseSize + Math.sin(effect.t * 0.01 + node.phase) * 2

        // Repel
        data.nodes.forEach((other, oIdx) => {
            if (idx === oIdx) return
            const dx = node.x - other.x
            const dy = node.y - other.y
            const dist = Math.hypot(dx, dy)
            if (dist < 80) {
                const force = (80 - dist) * 0.08
                node.vx += (dx / dist) * force
                node.vy += (dy / dist) * force
            }
        })

        // STRIKE PHASE (Last 40% of animation)
        if (tt > 0.6) {
            const dx = targetX - node.x
            const dy = targetY - node.y
            const dist = Math.hypot(dx, dy)

            // CONE FOCUS:
            // As we get closer to the end (tt -> 1.0), force the nodes to align with the target vector
            // effectively collapsing the "web" into a point/cone at the target.

            const speed = (tt - 0.6) * 3 // Ramp up speed significantly

            // Strong pull towards target
            node.vx += (dx / dist) * speed * 2
            node.vy += (dy / dist) * speed * 2

            // Dampen perpendicular velocity to "focus" the cone
            // (This makes them fly straight at the target instead of orbiting)
            node.vx *= 0.8
            node.vy *= 0.8

        } else {
            // Slow drift initially
            node.vx *= 0.95
            node.vy *= 0.95
        }

        node.x += node.vx
        node.y += node.vy
    })

    // Bubbling effect on impact (End of animation)
    if (tt > 0.85 && Math.random() < 0.5) {
        for (let k = 0; k < 4; k++) {
            const angle = Math.random() * Math.PI * 2
            const speed = 2 + Math.random() * 3
            spawnParticle({
                x: targetX + (Math.random() - 0.5) * 50,
                y: targetY + (Math.random() - 0.5) * 50,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 2, // Upward bias
                life: 500 + Math.random() * 300,
                color: Math.random() > 0.5 ? 'rgba(56, 189, 248, 0.9)' : 'rgba(15, 23, 42, 0.9)',
                size: 3 + Math.random() * 4
            })
        }
    }

    ctx.save()
    ctx.globalAlpha = Math.min(1, (1.2 - tt) * 2)

    // Draw Edge Connections (Lines from off-screen)
    data.edgeConnections.forEach(edge => {
        const node = data.nodes[edge.nodeIndex]
        ctx.beginPath()
        ctx.moveTo(edge.x, edge.y)
        const midX = (edge.x + node.x) / 2
        const midY = (edge.y + node.y) / 2
        // Tense curves
        const tension = Math.sin(effect.t * 0.005 + edge.nodeIndex) * (tt > 0.6 ? 5 : 30)
        ctx.quadraticCurveTo(midX + tension, midY + tension, node.x, node.y)
        ctx.strokeStyle = 'rgba(15, 23, 42, 0.8)' // Darker goo
        ctx.lineWidth = 3 + Math.sin(effect.t * 0.01) * 1
        ctx.stroke()

        // Pulses on edges
        // Logic: Wait at start, then rush
        edge.pulses.forEach(p => {
            if (tt > 0.5 && p.speed === 0) {
                // Release the pulses!
                p.speed = 0.03 + Math.random() * 0.04
            }

            if (p.speed > 0) {
                p.t += p.speed
                // Accelerate as they go
                p.speed *= 1.05
            }

            if (p.t > 1) {
                // Reset to start if early, else disappear
                if (tt < 0.8) { p.t = 0; p.speed = 0.03; }
                else return
            }

            const t = p.t
            const px = (1 - t) * (1 - t) * edge.x + 2 * (1 - t) * t * (midX + tension) + t * t * node.x
            const py = (1 - t) * (1 - t) * edge.y + 2 * (1 - t) * t * (midY + tension) + t * t * node.y

            const pulseSprite = getPsyPulseSprite();
            ctx.drawImage(pulseSprite, px - 15, py - 15);
        })
    })

    // Draw Internal Connections
    data.connections.forEach(conn => {
        const n1 = data.nodes[conn.from]
        const n2 = data.nodes[conn.to]
        ctx.beginPath()
        ctx.moveTo(n1.x, n1.y)
        ctx.lineTo(n2.x, n2.y)
        ctx.strokeStyle = 'rgba(30, 41, 59, 0.7)'
        ctx.lineWidth = 2
        ctx.stroke()
    })

    // Draw Nodes
    data.nodes.forEach(node => {
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(15, 23, 42, 0.95)'
        ctx.fill()

        // Inner glow
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.size * 0.3, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(56, 189, 248, 0.6)'
        ctx.fill()
    })

    ctx.restore()
    return true
}
